import { Fragment, type ReactElement, type ReactNode } from 'react';
import { MoreVertical } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface RowActionItem {
  /** Menu item text — also used as the React key, so keep it unique per menu. */
  readonly label: string;
  /** Optional leading icon (render at h-3.5 w-3.5 to match the other items). */
  readonly icon?: ReactNode;
  readonly onSelect: () => void;
  /** Destructive tone (red) — for withdraw / delete style actions. */
  readonly destructive?: boolean;
  readonly disabled?: boolean;
  /** Draw a separator above this item to visually group it (e.g. before a destructive action). */
  readonly separatorBefore?: boolean;
}

interface RowActionsMenuProps {
  /** Actions to list. Falsy entries are filtered out so callers can inline conditionals. */
  readonly actions: readonly (RowActionItem | false | null | undefined)[];
  /** Accessible label for the trigger button (e.g. "Consent actions"). */
  readonly label: string;
  readonly align?: 'start' | 'end';
  /** Extra classes for the trigger button. */
  readonly className?: string;
}

/**
 * A kebab (⋮) overflow menu for per-row actions in a list — the industry-standard,
 * label-every-action alternative to ambiguous icon-only row buttons. Built on the
 * shared `DropdownMenu`; drop it at the end of a list row and pass a declarative
 * `actions` array. Renders nothing when no actions remain after filtering.
 */
export function RowActionsMenu({
  actions,
  label,
  align = 'end',
  className,
}: RowActionsMenuProps): ReactElement | null {
  const items = actions.filter((a): a is RowActionItem => Boolean(a));
  if (items.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className={cn(
            'h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-foreground',
            className,
          )}
          aria-label={label}
        >
          <MoreVertical className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-[10rem]">
        {items.map((item, index) => (
          <Fragment key={item.label}>
            {item.separatorBefore === true && index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              disabled={item.disabled === true}
              onSelect={() => {
                item.onSelect();
              }}
              className={cn(item.destructive === true && 'text-destructive focus:text-destructive')}
            >
              {item.icon}
              {item.label}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
